import type { TranslationKey } from '@/i18n/translations'
import { diag, diagLine, diagnosticsRunStarted } from '@/lib/diagnostics'
import { gateMindsetFrames } from './mindset-frame-gate'
import type { ScanEventDescription } from './fh-vitals-events'
import type {
  HybridPhase,
  HybridSessionConfiguration,
  HybridSessionOutcome,
  MindsetVitalFamily,
  ShenAiCredentials,
} from './hybrid-session'
import { composeHybridSubmitRequest } from './hybrid-result'
import type { MindsetDemographics, MindsetPreparation } from './mindset-session'
import { measureMindsetSession, observeMindsetClient, prepareMindsetSession } from './mindset-session'
import { toSubmitRequestFromMindset } from './mindset-result'
import type { MindsetStopPayload, MindsetVitalClient } from './mindset-types'
import { toSubmitRequestFromShenAi } from './shenai-result'
import { pollShenAiUntilFinished, prepareShenAiSession } from './shenai-session'
import type { ShenAiVitalClient } from './shenai-types'
import { mindsetVendorReading, shenAiVendorReading } from './vendor-readings'

/**
 * The vitals Mindset is asked for: pulse, respiration AND blood pressure, by prefix (KAN-3, D-62).
 *
 * D-38 dropped `BP_*` because the composite read Shen.AI's pressure and Mindset's was discarded unread.
 * It is no longer discarded: both vendors' readings are stored whole (`vendorReadings`), because they are
 * not interchangeable. The cost D-38 named is real - one more ONNX inference per frame - and with the
 * frame gate it now shows as a lower frame rate for Mindset rather than as a growing queue; the
 * diagnostics panel's `answeredByWorker` is where to read it. Blood oxygen stays out: not asked for.
 */
const MINDSET_VITAL_FAMILIES_THIS_SCAN_READS: readonly MindsetVitalFamily[] = ['PR', 'RR', 'BP']

/** Least advanced first: the screen shows whichever vendor is further BEHIND (docs/decisions.md D-37). */
const PHASE_ORDER: readonly HybridPhase[] = ['loading', 'licensing', 'measuring']

type Vendor = 'shenAi' | 'mindset'

/**
 * Runs BOTH SDKs over ONE camera AT THE SAME TIME - the arrangement D-37 built and D-42 replaced with
 * a sequence, restored behind `hybridMode: "concurrent"` in /config.json for KAN-3.
 *
 * **Why it exists again.** D-42 retired it on one finding: a real iPhone killed the tab, about two runs
 * in three, because two WASM heaps beside a WebGL2 pipeline exceed WebKit's per-tab ceiling. That is a
 * fact about one device class, and the question KAN-3 asks - can both SDKs share one camera capture
 * in one run - has a different answer on a desktop browser, an Android phone or a newer iPhone. This
 * runner is how that is measured rather than argued. The sequential runner stays the default and is
 * untouched (hybrid-session.ts).
 *
 * The rules are D-37's, and each is restated where it is implemented:
 *
 * 1. **One camera, opened once, split.** Not this file's job - the flow opens the camera in the tap
 *    and hands Shen.AI a CLONE (patient-camera.ts). Both engines read the same frames.
 * 2. **Both ready before either measures.** The two prologues run concurrently and are a barrier:
 *    starting the first as soon as it was ready would be two scans of two different minutes.
 * 3. **The platform reads a composite, and both vendors' own readings travel beside it** - Shen.AI's
 *    pressure beside Mindset's pulse and respiration (hybrid-result.ts), plus `vendorReadings`: each
 *    vendor's whole reading, because the two are not interchangeable (D-62). A PARTIAL scan is submitted
 *    rather than repeated: when no composite can be built, the one vendor whose own reading is complete
 *    is submitted (Shen.AI first), and the other is marked `measured: false`.
 * 4. **The scan ends when Shen.AI has finished AND Mindset has settled its heart rate or stopped** -
 *    or when the hold window closes. Mindset's own session runs to its LICENCE's window (D-40), so
 *    waiting for it to stop of its own accord would hold the patient for 110 seconds; its `S-HRT-061`
 *    is the vendor saying its pulse is done (D-42).
 *
 * One thing D-37 did not have: **Mindset's frames are gated** (mindset-frame-gate.ts). Its pump hands
 * its worker every camera frame - cloned, on iOS - with nothing waiting for the worker, and beside
 * Shen.AI that worker falls behind; the unbounded queue that builds is the likeliest cause of the tab
 * kills D-38 recorded (D-62's addendum).
 *
 * What the patient sees follows D-37's "slower half" policy: the less advanced phase, the smaller of
 * the two progress fractions (a vendor that reports none does not hold the bar back), and both vendors'
 * signals merged by sentence, because both are looking at the same face.
 *
 * A Mindset that cannot get ready does not cost the scan: Shen.AI measures alone and its reading is
 * submitted, as it would be on the sequential path. A Shen.AI that cannot get ready ends the scan,
 * because there is no blood pressure without it.
 */
export async function runConcurrentHybridScanSession(
  mindsetClient: MindsetVitalClient,
  shenAiClient: ShenAiVitalClient,
  demographics: MindsetDemographics,
  shenAiCredentials: ShenAiCredentials,
  config: HybridSessionConfiguration,
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  onProgress?: (fractionComplete: number) => void,
  onPhase?: (phase: HybridPhase) => void,
  /** Mindset's own result, for this installation's forward to the vendor's records (D-33). */
  onMindsetVendorResult?: (result: unknown) => void,
): Promise<HybridSessionOutcome> {
  const phases: Record<Vendor, HybridPhase> = { shenAi: 'loading', mindset: 'loading' }
  let shownPhase: HybridPhase | null = null
  const showPhase = (vendor: Vendor, next: HybridPhase) => {
    phases[vendor] = next
    const slower =
      PHASE_ORDER.indexOf(phases.shenAi) <= PHASE_ORDER.indexOf(phases.mindset) ? phases.shenAi : phases.mindset
    diag('phase', { shown: slower, ...phases })
    if (slower !== shownPhase) {
      shownPhase = slower
      console.log('[hybrid:concurrent] phase', slower, { ...phases })
      onPhase?.(slower)
    }
  }

  const progress: Record<Vendor, number | null> = { shenAi: null, mindset: null }
  const showProgress = (vendor: Vendor, fraction: number) => {
    progress[vendor] = fraction
    diag('progress', { shenAi: progress.shenAi?.toFixed(2), mindset: progress.mindset?.toFixed(2) })
    const reported = [progress.shenAi, progress.mindset].filter((value): value is number => value !== null)
    onProgress?.(Math.min(...reported))
  }

  const signals: Record<Vendor, ScanEventDescription[]> = { shenAi: [], mindset: [] }
  const showSignals = (vendor: Vendor, descriptions: ScanEventDescription[]) => {
    signals[vendor] = descriptions
    const seen = new Set<TranslationKey>()
    onSignals?.(
      [...signals.shenAi, ...signals.mindset].filter((description) => {
        if (seen.has(description.messageKey)) return false
        seen.add(description.messageKey)
        return true
      }),
    )
  }

  diagnosticsRunStarted('hybrid:concurrent')

  // BEFORE the vendor first asks the element for a frame, which is its preview mode in the prologue.
  // Without it Mindset's worker inbox is the unbounded queue that killed the tab (mindset-frame-gate.ts).
  const gate = gateMindsetFrames(config.mindset.video, mindsetClient)
  const startedAt = Date.now()
  let lastLoggedAt = 0
  const ticker = setInterval(() => {
    const stats = gate.stats()
    const shenAi = { measurement: shenAiClient.measurementState(), face: shenAiClient.faceState() }
    diag('mindset frames', stats)
    diag('shenai state', shenAi)
    if (Date.now() - lastLoggedAt >= 5_000) {
      lastLoggedAt = Date.now()
      console.log('[hybrid:concurrent] frames', { ...stats, shenAi, afterMs: Date.now() - startedAt })
    }
  }, 1_000)

  // Every step of Mindset's prologue, timed, on the diagnostics panel - so a Mindset that never starts
  // measuring says WHERE it stopped, on a phone nobody can attach a debugger to.
  const steps: string[] = []
  const step = (name: string) => {
    steps.push(`${name} ${((Date.now() - startedAt) / 1000).toFixed(1)}s`)
    diag('mindset steps', steps.join(' → '))
  }
  mindsetClient = traceMindsetClient(mindsetClient, step)
  mindsetClient.on('ready', () => step('ready'))
  // The vendor's worker log on the panel: on iOS its first `loadWasmModule` fails and is retried (two
  // `webvital.js` loads 330 ms apart, observed), and the reason exists only in that worker's console.
  mindsetClient.on('log', (entry) => diagLine('mindset log', describeMindsetWorkerLog(entry)))
  mindsetClient.on('error', (error) => diagLine('mindset log', `ERROR ${typeof error === 'string' ? error : (error?.message ?? 'unknown')}`))
  mindsetClient.on('authorizedVitals', (vitals) => step(`vitals[${vitals.join(',')}]`))

  // Subscribed BEFORE init, as observeMindsetClient requires - which on this path is before anything.
  const observation = observeMindsetClient(
    mindsetClient,
    (descriptions) => showSignals('mindset', descriptions),
    (fraction) => showProgress('mindset', fraction),
  )

  try {
    // ---- The prologue: both vendors at once, and a barrier --------------------------------------------
    const startedLoading = Date.now()
    const [shenAi, mindset] = await Promise.all([
      prepareShenAiSession(
        shenAiClient,
        shenAiCredentials.token,
        shenAiCredentials.userId,
        config.shenAi,
        (fraction) => showProgress('shenAi', fraction),
        (phase) => showPhase('shenAi', phase),
      ),
      // A Mindset that THROWS while getting ready - its licence refused, its worker dead - is a Mindset
      // that did not get ready, not a scan that failed: Shen.AI measures alone and the reading is marked
      // partial (D-62). Its `authorize()` throws "Unable to verify the license key" on a refused licence,
      // which is exactly what a live run hit, and let through it took Shen.AI's measurement down with it.
      prepareMindsetSession(mindsetClient, observation, config.mindset, (phase) => showPhase('mindset', phase)).catch(
        (error: unknown): MindsetPreparation => {
          console.warn('[hybrid:concurrent] mindset failed while getting ready', error)
          showPhase('mindset', 'measuring')
          return { prepared: false, messageKey: 'scanCouldNotStart' }
        },
      ),
    ])

    console.log('[hybrid:concurrent] both prologues ended', {
      afterMs: Date.now() - startedLoading,
      shenAi: shenAi.prepared ? 'ready' : shenAi.messageKey,
      mindset: mindset.prepared ? mindset.vitals : mindset.messageKey,
    })

    if (!shenAi.prepared) {
      return { outcome: 'error', messageKey: shenAi.messageKey }
    }

    const families = config.mindsetVitalFamilies ?? MINDSET_VITAL_FAMILIES_THIS_SCAN_READS
    const vitals = mindset.prepared
      ? mindset.vitals.filter((vital) => families.some((family) => vital.startsWith(`${family}_`)))
      : []
    if (vitals.length === 0) {
      console.warn('[hybrid:concurrent] mindset will not measure, so shenai measures alone', {
        reason: mindset.prepared ? 'licensed for nothing this scan reads' : mindset.messageKey,
      })
      // TORN DOWN NOW, not in `finally`. A Mindset refused its licence is still in preview mode, still
      // pumping every frame through face detection and segmentation - a live run measured ~13 fps of it
      // for the whole minute Shen.AI was measuring alone, CPU and memory spent beside the one engine that
      // could still produce a reading. `finally` destroys it again, which is a no-op.
      try {
        mindsetClient.destroy()
      } catch {
        // Nothing to salvage; Shen.AI measures either way.
      }
      gate.release()
    }

    // ---- The measurement: both engines over the same frames --------------------------------------------
    const startedHolding = Date.now()
    const heldFor = () => Date.now() - startedHolding
    const remainingHold = () => Math.max(0, config.holdWindowMs - heldFor())

    showPhase('shenAi', 'measuring')
    showPhase('mindset', 'measuring')
    progress.shenAi = null
    shenAiClient.startMeasurement()

    const mindsetMeasuring =
      vitals.length > 0
        ? measureMindsetSession(mindsetClient, observation, vitals, demographics, {
            ...config.mindset,
            measureWindowMs: config.holdWindowMs,
          })
        : null
    // Never awaited past the point the scan has moved on, so a stop() on a client already torn down must
    // not surface as an unhandled rejection.
    mindsetMeasuring?.catch((error: unknown) => console.warn('[hybrid:concurrent] mindset measurement threw', error))

    const shenAiFinishedItself = await pollShenAiUntilFinished(
      shenAiClient,
      config.shenAi,
      (descriptions) => showSignals('shenAi', descriptions),
      (fraction) => showProgress('shenAi', fraction),
    )
    if (!shenAiFinishedItself) {
      shenAiClient.stopMeasurement()
    }
    const shenAiResults = shenAiClient.results()
    console.log('[hybrid:concurrent] shenai finished', { afterMs: heldFor(), result: shenAiResults })

    let mindsetStop: MindsetStopPayload | null = null
    if (mindsetMeasuring) {
      const ending = await Promise.race([
        observation.hrSettled.then(() => 'the vendor settled its heart rate'),
        mindsetMeasuring.then(() => 'the vendor stopped itself'),
        new Promise<string>((resolve) => setTimeout(() => resolve('the hold window closed'), remainingHold())),
      ])
      mindsetStop = observation.stopped() ?? (await stopQuietly(mindsetClient))
      console.log('[hybrid:concurrent] mindset ended because', ending, {
        afterMs: heldFor(),
        result: mindsetStop?.result,
      })
      if (mindsetStop?.result) {
        onMindsetVendorResult?.(mindsetStop.result)
      }
    }

    // ---- The reading ----------------------------------------------------------------------------------
    const mindsetValues = mindsetStop?.result?.finalVitalsMeasurementValues
    const composition = composeHybridSubmitRequest({ shenAi: shenAiResults, mindset: mindsetValues })
    const shenAiAlone = toSubmitRequestFromShenAi(shenAiResults)
    const mindsetAlone = toSubmitRequestFromMindset(mindsetValues)

    // BOTH vendors' own readings travel with whatever is submitted, a vendor that produced nothing
    // included - `measured: false` is how a partial scan is marked (D-62).
    const vendorReadings = [shenAiVendorReading(shenAiResults), mindsetVendorReading(mindsetValues)]

    console.log('[hybrid:concurrent] what each vendor measured', {
      vendorReadings,
      composite: composition.composed ? composition.request : { missing: composition.missing },
    })
    diag('readings', vendorReadings.map((reading) => `${reading.vendor}:${reading.measured ? 'yes' : 'NO'}`).join(' '))

    // A partial scan is SUBMITTED rather than offered again (the product owner's call, D-62): the
    // composite when both vendors delivered their assigned vitals, otherwise the one vendor whose own
    // reading is complete - Shen.AI first, because its reading carries a measured pressure.
    const submitted = composition.composed ? composition.request : (shenAiAlone ?? mindsetAlone)
    if (!submitted) {
      return { outcome: 'no-usable-result' }
    }
    if (composition.composed) {
      console.log('[hybrid:concurrent] submitting the composite', composition.attribution)
    } else {
      console.warn('[hybrid:concurrent] no composite - a partial scan is submitted', {
        missing: composition.missing,
        from: shenAiAlone ? 'ShenAi' : 'Mindset',
      })
    }
    return { outcome: 'measured', request: { ...submitted, vendorReadings } }
  } catch (error) {
    console.warn('[hybrid:concurrent] the session threw', error)
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  } finally {
    clearInterval(ticker)
    console.log('[hybrid:concurrent] frames at the end', { ...gate.stats(), afterMs: Date.now() - startedAt })
    diag('mindset frames', gate.stats())
    diag('ended', `+${((Date.now() - startedAt) / 1000).toFixed(1)}s`)
    gate.release()
    try {
      shenAiClient.destroy()
    } catch {
      // Nothing to salvage; the other vendor still has to be torn down.
    }
    try {
      mindsetClient.destroy()
    } catch {
      // Nothing to salvage from this one either.
    }
  }
}

/**
 * One worker log entry as a line of text: the error when there is one, a few fields of the WASM
 * environment report (memory, JIT), or the message. Strings only - nothing of the entry is retained.
 */
export function describeMindsetWorkerLog(entry: unknown): string {
  const record = (typeof entry === 'object' && entry !== null ? entry : { msg: entry }) as Record<string, unknown>
  if (record.error) return `ERROR ${String(record.error)}`
  const report = record.msg && typeof record.msg === 'object' ? (record.msg as Record<string, unknown>) : record
  if (report.wasmEnvReport && typeof report.wasmEnvReport === 'object') {
    const env = report.wasmEnvReport as Record<string, unknown>
    return `env jitDisabled=${String(env.likelyJITDisabled)} wasmLoopMs=${String(env.wasm1MCallsDurationMs)} memory=${JSON.stringify(env.memory ?? env.memoryEstimate ?? null)?.slice(0, 80)}`
  }
  if (report.wasmEnvReportError) return `ERROR env report: ${String(report.wasmEnvReportError)}`
  const text = typeof report.msg === 'string' ? report.msg : JSON.stringify(report)
  return text.slice(0, 160)
}

/** The same client, with each prologue and measurement call reported as it starts, ends or throws. */
function traceMindsetClient(client: MindsetVitalClient, step: (name: string) => void): MindsetVitalClient {
  const traced = <A extends unknown[], R>(name: string, call: (...args: A) => Promise<R>) =>
    async (...args: A): Promise<R> => {
      step(name)
      try {
        const result = await call(...args)
        step(`${name}✓`)
        return result
      } catch (error) {
        step(`${name}✗`)
        throw error
      }
    }
  return {
    ...client,
    on: client.on.bind(client),
    init: traced('init', client.init.bind(client)),
    startPreviewMode: traced('preview', client.startPreviewMode.bind(client)),
    authorize: traced('authorize', client.authorize.bind(client)),
    start: traced('start', client.start.bind(client)),
    stop: traced('stop', client.stop.bind(client)),
    destroy: client.destroy.bind(client),
  }
}

async function stopQuietly(client: MindsetVitalClient): Promise<MindsetStopPayload | null> {
  try {
    return await client.stop()
  } catch (error) {
    console.warn('[hybrid:concurrent] mindset threw while being stopped', error)
    return null
  }
}
